import { lazyWithRetry } from "utils/lazy-with-retry";
import { FEATURE_FLAGS } from "config/feature-flags";
import Home from "components/home";

const ROUTE_TYPES = {
  PUBLIC: "public",
  PROTECTED: "protected",
  AUTH: "auth",
};

// Component imports with layouts grouping
const routes = {
  blankLayout: [
    {
      path: "*",
      element: lazyWithRetry(() => import("components/not-found")),
      isProtected: false,
      title: "Page Not Found - Kalyanawaran",
      screen: "Not Found",
    },
    {
      path: "/404",
      element: lazyWithRetry(() => import("components/not-found")),
      isProtected: false,
      title: "Page Not Found - Kalyanawaran",
      screen: "Not Found",
    },
  ],
  defaultLayout: [
    {
      // Eagerly imported so the LCP banner img renders without waiting for a lazy chunk
      path: "/",
      element: Home,
      isProtected: false,
      title: "Kalyanawaran - Matrimonial Service",
      description:
        "Aims to provide a centralized platform where brides and grooms can meet and communicate with each other.",
      screen: "Home",
    },
    {
      path: "/register",
      element: lazyWithRetry(() => import("components/register")),
      isProtected: false,
      title: "Register - Kalyanawaran",
      description:
        "Register to find your perfect match. Join Kalyanawaran today!",
      screen: "Register",
    },
    {
      path: "/login",
      element: lazyWithRetry(() => import("components/login")),
      isProtected: false,
      title: "Login - Kalyanawaran",
      description:
        "Login to your Kalyanawaran account to find your life partner.",
      screen: "Login",
    },
    {
      path: "/profileedit",
      element: lazyWithRetry(() => import("view/profile/edit")),
      isProtected: true,
      title: "Edit Profile - Kalyanawaran",
      description:
        "Edit and update your profile details on Kalyanawaran Matrimony.",
      screen: "Profile Edit",
    },
    {
      path: "/forgetpassword",
      element: lazyWithRetry(() => import("components/forget-password")),
      isProtected: false,
      title: "Forgot Password - Kalyanawaran",
      description: "Recover your Kalyanawaran account password.",
      screen: "Forgot Password",
    },
    {
      path: "/search-profiles",
      element: lazyWithRetry(() => import("view/search-profile")),
      isProtected: true,
      title: "Search Profiles - Kalyanawaran",
      description:
        "Search and browse profiles of prospective brides and grooms on Kalyanawaran.",
      screen: "Search Profiles",
    },
    {
      path: "/members/:id",
      element: lazyWithRetry(() => import("view/members/members-pages")),
      isProtected: true,
      title: "Member Profile - Kalyanawaran",
      description:
        "View details of members looking for a life partner on Kalyanawaran Matrimony.",
      screen: "Member Profile",
    },
    {
      path: "/terms",
      element: lazyWithRetry(() => import("components/terms")),
      isProtected: false,
      title: "Terms & Conditions - Kalyanawaran",
      description:
        "Read the Terms and Conditions of membership in Kalyanawaran.",
      screen: "Terms",
    },
    {
      path: "/privacy-policy",
      element: lazyWithRetry(() => import("components/privacy-policy")),
      isProtected: false,
      title: "Privacy Policy - Kalyanawaran",
      description: "Read the Privacy Policy of Kalyanawaran Matrimony.",
      screen: "Privacy Policy",
    },
    {
      path: "/blog",
      element: lazyWithRetry(() => import("view/blog/blog-index")),
      isProtected: false,
      title: "Matrimony Blog - Kalyanawaran",
      description:
        "Explore astrology, horoscope dosham, Tamil wedding traditions and matrimonial tips on the Kalyanawaran blog.",
      screen: "Blog Index",
    },
    {
      path: "/blog/dosham",
      element: lazyWithRetry(() => import("view/blog/dosham")),
      isProtected: false,
      title:
        "Horoscope Doshams in Kalyanawaran Matrimony - Sevvai, Rahu Ketu, Kalathira - Kalyanawaran",
      description:
        "Learn about the main types of horoscope doshams (blemishes) in Tamil Vedic astrology, including Sevvai, Rahu-Ketu, Kalathira, Mangalya, Pithru, and Surya dosham.",
      screen: "Blog Dosham",
    },
    {
      path: "/blog/nakshatra-porutham",
      element: lazyWithRetry(() => import("view/blog/nakshatra-porutham")),
      isProtected: false,
      title:
        "10 Nakshatra Poruthams in Kalyanawaran Matrimony - Complete Guide | Kalyanawaran",
      description:
        "Learn about all 10 Nakshatra Poruthams (Dina, Gana, Mahendra, Stree Deergha, Yoni, Rasi, Rajju, Vashya, Varna, Nadi) used in Tamil Vedic marriage matching. Includes Vedha Dosha explained.",
      screen: "Blog Nakshatra Porutham",
    },
    {
      path: "/cookie-policy",
      element: lazyWithRetry(() => import("components/cookie-policy")),
      isProtected: false,
      title: "Cookie Policy - Kalyanawaran",
      description: "Read the Cookie Policy of Kalyanawaran Matrimony.",
      screen: "Cookie Policy",
    },
    ...(FEATURE_FLAGS.ENABLE_FAQ
      ? [
          {
            path: "/faq",
            element: lazyWithRetry(() => import("view/faq")),
            isProtected: false,
            title: "Frequently Asked Questions (FAQ) - Kalyanawaran",
            description:
              "Find answers to common questions about registration, free & paid membership plans, profile verification, partner search, password resets, and customer support on Kalyanawaran Matrimony.",
            screen: "FAQ",
          },
          {
            path: "/faqs",
            element: lazyWithRetry(() => import("view/faq")),
            isProtected: false,
            title: "Frequently Asked Questions (FAQ) - Kalyanawaran",
            description:
              "Find answers to common questions about registration, free & paid membership plans, profile verification, partner search, password resets, and customer support on Kalyanawaran Matrimony.",
            screen: "FAQs",
          },
        ]
      : []),
    ...(FEATURE_FLAGS.ENABLE_PREFERENCE
      ? [
          {
            path: "/preference",
            element: lazyWithRetry(
              () => import("view/preference/preference-pages")
            ),
            isProtected: true,
            title: "Preferences - Kalyanawaran",
            description:
              "Set your preferences to find your perfect match on Kalyanawaran.",
            screen: "Preferences",
          },
        ]
      : []),
    {
      path: "/resetPassword/:token",
      element: lazyWithRetry(() => import("components/reset-password")),
      isProtected: false,
      title: "Reset Password - Kalyanawaran",
      description: "Reset your Kalyanawaran account password.",
      screen: "Reset Password",
    },
    {
      path: "/emailVerification/:token",
      element: lazyWithRetry(() => import("components/email-verify")),
      isProtected: true,
      title: "Email Verification - Kalyanawaran",
      description:
        "Verify your email address to activate your Kalyanawaran account.",
      screen: "Email Verification",
    },
    {
      path: "/contact-us",
      element: lazyWithRetry(() => import("view/contact-us/contact-us")),
      isProtected: false,
      title: "Contact Us - Kalyanawaran",
      description: "Contact Kalyanawaran for any queries or support.",
      screen: "Contact Us",
    },
    {
      path: "/about-us",
      element: lazyWithRetry(() => import("view/about-us")),
      isProtected: false,
      title: "About Us - Kalyanawaran",
      description: "Learn more about Kalyanawaran Matrimony services.",
      screen: "About Us",
    },
    {
      path: "/nakshatra-rashi-list",
      element: lazyWithRetry(() => import("view/nakshatra-rashi-list")),
      isProtected: false,
      title: "Nakshatra Rashi List - Kalyanawaran",
      description:
        "Check the list of Nakshatras and their corresponding Rashis.",
      screen: "Nakshatra Rashi List",
    },
    {
      path: "/quick-upload",
      element: lazyWithRetry(() => import("view/quick-upload")),
      isProtected: false,
      title: "Quick Upload - Kalyanawaran",
      description: "Quickly upload your profile details to Kalyanawaran.",
      screen: "Quick Upload",
    },
    {
      path: "/age-difference-calculator",
      element: lazyWithRetry(() => import("view/age-difference-calculator")),
      isProtected: false,
      title: "Age Difference Calculator - Kalyanawaran",
      description:
        "Calculate the age difference between bride and groom easily. Check age details for matrimonial matches on Kalyanawaran.",
      screen: "Age Difference Calculator",
    },
    {
      path: "/age-different-calculator",
      element: lazyWithRetry(() => import("view/age-difference-calculator")),
      isProtected: false,
      title: "Age Difference Calculator - Kalyanawaran",
      description:
        "Calculate the age difference between bride and groom easily. Check age details for matrimonial matches on Kalyanawaran.",
      screen: "Age Different Calculator",
    },
    {
      path: "/age-calculator",
      element: lazyWithRetry(() => import("view/age-calculator")),
      isProtected: false,
      title: "Age Calculator - Kalyanawaran",
      description:
        "Calculate your exact age in years, months, and days. Find out the day of the week you were born and countdown to your next birthday.",
      screen: "Age Calculator",
    },
    {
      path: "/gotralu",
      element: lazyWithRetry(() => import("components/gotralu/gotralu")),
      isProtected: false,
      title: "Gotralu - Kalyanawaran",
      description: "Lineages of the 102 Rishis - Arya Vysya Gotralu",
      screen: "Gotralu",
    },
    {
      path: "/social-groups",
      element: lazyWithRetry(() => import("view/social-groups")),
      isProtected: false,
      title: "Social Groups - Kalyanawaran",
      description:
        "Join social groups and connect with members on Kalyanawaran.",
      screen: "Social Groups",
    },
    // Commented routes for future implementation
    // { path: "/counter", element: lazyWithRetry(() => import("components/counter")), isProtected: false, title: "Counter - Kalyanawaran", screen: "Counter" },
    {
      path: "/verify",
      element: lazyWithRetry(() => import("view/account/verification")),
      isProtected: true,
      title: "Verification - Kalyanawaran",
      description:
        "Verify your mobile number and email address on Kalyanawaran.",
      screen: "Verification",
    },
    // { path: "/settings", element: lazyWithRetry(() => import("components/settings")), isProtected: false, title: "Settings - Kalyanawaran", screen: "Settings" },
    {
      path: "/account",
      element: lazyWithRetry(() => import("view/account/information")),
      isProtected: true,
      title: "Account Information - Kalyanawaran",
      description:
        "Manage your account information and settings on Kalyanawaran.",
      screen: "Account Settings",
    },
    {
      path: "/dashboard/*",
      element: lazyWithRetry(() => import("view/dashboard/dashboard")),
      isProtected: true,
      title: "User Dashboard - Kalyanawaran",
      description:
        "Access your Kalyanawaran user dashboard for profile management.",
      screen: "Dashboard",
    },
    {
      path: "/change-password",
      element: lazyWithRetry(() => import("view/account/change-password")),
      isProtected: true,
      title: "Change Password - Kalyanawaran",
      description: "Change your Kalyanawaran account password.",
      screen: "Change Password",
    },
    {
      path: "/testimonial",
      element: lazyWithRetry(() => import("view/testimonial")),
      isProtected: true,
      title: "Success Stories - Kalyanawaran",
      description:
        "Share your wedding story and matrimonial experience with Kalyanawaran.",
      screen: "Testimonial",
    },
    {
      path: "/plans",
      element: lazyWithRetry(() => import("view/plans")),
      isProtected: false,
      title: "Membership Plans & Shubh Arambh Offer - Kalyanawaran",
      description:
        "Explore Shubh Arambh (Silver Plan) offer at ₹365/year (₹1/day), Free plan, and membership tiers on Kalyanawaran Matrimony.",
      screen: "Plans",
    },
    {
      path: "/pricing",
      element: lazyWithRetry(() => import("view/plans")),
      isProtected: false,
      title: "Membership Plans & Shubh Arambh Offer - Kalyanawaran",
      description:
        "Explore Shubh Arambh (Silver Plan) offer at ₹365/year (₹1/day), Free plan, and membership tiers on Kalyanawaran Matrimony.",
      screen: "Pricing",
    },
    {
      path: "/success-stories",
      element: lazyWithRetry(() => import("view/success-stories")),
      isProtected: false,
      title: "Success Stories - Kalyanawaran",
      description: "View success stories and happy couples from Kalyanawaran.",
      screen: "Success Stories",
    },
  ],
  // Dashboard sub-routes — used by AnalyticsTracker and SEO for screen name resolution
  // Rendering is still handled by the wildcard /dashboard/* route above
  metadataSubRoutes: [
    {
      path: "/dashboard/mutual-interest",
      isProtected: true,
      title: "Mutual Interest - Dashboard",
      screen: "Dashboard - Mutual Interest",
    },
    {
      path: "/dashboard/interest-sent",
      isProtected: true,
      title: "Interest Sent - Dashboard",
      screen: "Dashboard - Interest Sent",
    },
    {
      path: "/dashboard/interest-received",
      isProtected: true,
      title: "Interest Received - Dashboard",
      screen: "Dashboard - Interest Received",
    },
    {
      path: "/dashboard/matching-profile",
      isProtected: true,
      title: "Matching Profiles - Dashboard",
      screen: "Dashboard - Matching Profiles",
    },
    {
      path: "/dashboard/my-visitor",
      isProtected: true,
      title: "My Visitors - Dashboard",
      screen: "Dashboard - My Visitors",
    },
    {
      path: "/dashboard/photo-request",
      isProtected: true,
      title: "Photo Requests - Dashboard",
      screen: "Dashboard - Photo Requests",
    },
  ],
};

export { ROUTE_TYPES };
export default routes;
